import {
  createRootRoute,
  HeadContent,
  Link,
  Scripts
} from '@tanstack/react-router';
import { AppProviders } from 'layouts/AppProviders';
import { type ReactNode } from 'react';
import { localBusinessJsonLd, SITE_URL } from 'seo/jsonld';
import '../main.css';

/**
 * Tailwind-only (renders at the root, outside the dashboard's MUI theme
 * boundary, and inside prerendered pages).
 */
const NotFoundPage = () => (
  <div className="flex h-dvh w-full items-center justify-center bg-[#f2f3f1] font-sans">
    <div className="mx-4 max-w-md py-12 text-center">
      <p className="font-mono text-sm font-bold tracking-[0.2em] text-[#ea580c]">404</p>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-[#111827]">Page not found</h1>
      <p className="mt-4 text-base text-[#64748b]">
        Sorry, we couldn&rsquo;t find the page you&rsquo;re looking for.
      </p>
      <div className="mt-8 flex justify-center gap-3">
        <Link
          to="/"
          className="rounded-md bg-[#ea580c] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#c2410c]"
        >
          Go back home
        </Link>
        <Link
          to="/contact"
          className="rounded-md px-6 py-3 text-sm font-semibold text-[#1a202c] transition-colors hover:text-[#ea580c]"
        >
          Contact us →
        </Link>
      </div>
    </div>
  </div>
);

/**
 * Document shell. Replaces the old index.html: every tag that lived there
 * (meta, favicons, fonts, LocalBusiness schema, the #app/#print mount points)
 * now renders from here so prerendered pages carry it in their HTML.
 * Landing routes set their own title/description/canonical via head().
 */
const RootDocument = ({ children }: { children: ReactNode }) => (
  <html lang="en-NZ">
    <head>
      <HeadContent />
    </head>
    <body>
      {/* Print styles hide #app and reveal #print (PrintableContent). */}
      <div id="app">{children}</div>
      <div id="print" />
      <Scripts />
    </body>
  </html>
);

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      {
        title:
          'Dreams Built | Concrete Foundations, Driveways & Landscaping – Waikato, NZ'
      },
      {
        name: 'description',
        content:
          'Dreams Built specialises in expert concrete foundations, driveways, patios, fencing and landscaping for residential and commercial projects across Waikato and Hamilton, New Zealand.'
      },
      {
        name: 'keywords',
        content:
          'concrete foundations, driveways, patios, fencing, landscaping, Waikato, Hamilton, Morrinsville, Cambridge, NZ'
      },
      { name: 'author', content: 'Dreams Built' },
      {
        property: 'og:title',
        content:
          'Dreams Built | Concrete Foundations & Construction – Waikato, NZ'
      },
      {
        property: 'og:description',
        content:
          'Expert concrete foundations, driveways, patios, fencing and landscaping across Waikato and Hamilton, New Zealand.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${SITE_URL}/` },
      { property: 'og:image', content: `${SITE_URL}/poster.webp` },
      {
        property: 'og:image:alt',
        content: 'Dreams Built – Waikato concrete specialists'
      },
      { property: 'og:locale', content: 'en_NZ' },
      { property: 'og:site_name', content: 'Dreams Built' },
      { name: 'twitter:card', content: 'summary_large_image' },
      {
        name: 'twitter:title',
        content:
          'Dreams Built | Concrete Foundations & Construction – Waikato, NZ'
      },
      {
        name: 'twitter:description',
        content:
          'Expert concrete foundations, driveways, patios, fencing and landscaping for residential and commercial projects in Waikato and Hamilton, NZ.'
      },
      { name: 'twitter:image', content: `${SITE_URL}/poster.webp` },
      { name: 'theme-color', content: '#ffffff' }
    ],
    links: [
      /*
       * No canonical here. TanStack Router dedupes `meta` by name/property but
       * NOT `links` by `rel`, so a root canonical does not lose to the one in
       * buildHead - both ship, and a page with two rel=canonical has all of
       * them ignored by Google. Every landing route sets its own via
       * seo/head.ts; the dashboard is robots-disallowed and needs none.
       */
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
      { rel: 'manifest', href: '/site.webmanifest' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous'
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Archivo:wdth,wght@62..125,100..900&family=IBM+Plex+Mono:wght@400;500&display=swap'
      }
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(localBusinessJsonLd)
      }
    ]
  }),
  shellComponent: RootDocument,
  component: AppProviders,
  notFoundComponent: () => <NotFoundPage />
});
